import { UserProfileRole } from '@@types/api.types';
import type {
  MissionAssessmentCoreSelectDto,
  MissionAssessmentListItemDto,
  TechnologySelectDto,
} from '@@types/api.types';
import {
  useMissionAssessmentCoreQueryOptions,
  useMissionAssessmentVersionsByTechnologyQueryOptions,
} from '@queries/mission-assessments.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import { useQuery } from '@tanstack/react-query';
import {
  TechnologyHeaderTab,
  parseAsMrlSubTab,
  parseAsTechnologyHeaderTab,
  parseAsTrlSubTab,
} from '@utils/query-state.util';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { defaultTab } from './components/technology-header.component';

type UseTechnologyPageParams = {
  technologyId?: string;
};

type UseTechnologyPageResult = {
  displayedMissionAssessment: MissionAssessmentCoreSelectDto | null;
  helmetTitle: string;
  isInnovator: boolean;
  isLoadingDisplayedMissionAssessment: boolean;
  isLoadingTechnology: boolean;
  isTechnologyAccessForbidden: boolean;
  isTransitioning: boolean;
  isVersionsLoading: boolean;
  maVersions: MissionAssessmentListItemDto[];
  onToggleTab: (tab: TechnologyHeaderTab) => void;
  selectedTab: TechnologyHeaderTab;
  shouldShowEmptyState: boolean;
  showTileLoading: boolean;
  technology?: TechnologySelectDto;
  technologyFallbackPath: string;
};

export const useTechnologyPage = (params: UseTechnologyPageParams): UseTechnologyPageResult => {
  const { technologyId } = params;
  const navigate = useNavigate();

  const [maId, setMaId] = useQueryState('maId', parseAsString);
  const [selectedTab, setSelectedTab] = useQueryState('tab', parseAsTechnologyHeaderTab.withDefault(defaultTab));

  // Subtab states for TRL and MRL
  const [, setTrlTab] = useQueryState('trlTab', parseAsTrlSubTab.withDefault('overview'));
  const [, setTrlAssessmentId] = useQueryState('assessmentId', parseAsString);
  const [, setMrlTab] = useQueryState('mrlTab', parseAsMrlSubTab.withDefault('overview'));
  const [, setMrlAssessmentId] = useQueryState('mrlAssessmentId', parseAsString);

  const {
    data: technology,
    error: technologyError,
    isError: isTechnologyError,
    isPending: isLoadingTechnology,
  } = useGetTechnology(technologyId);
  const { data: user, isExpert, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();

  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;
  const isIntel = user?.profile?.role === UserProfileRole.INTELLIGENCE;

  // Redirect innovators away from the organization tab
  useEffect(() => {
    if (isInnovator && selectedTab === 'organization') {
      void setSelectedTab(defaultTab);
    }
  }, [isInnovator, selectedTab, setSelectedTab]);

  const isTechnologyAccessForbidden = Boolean(
    (isExpert || isIntel) && isTechnologyError && technologyError?.errors?.[0]?.statusCode === 403,
  );
  const shouldFetchMissionAssessments =
    !isSelectedCompanyParentOrganization || Boolean(selectedCompany?.id && technology?.company?.id);

  // Fetch versions list (lightweight) to get available MA IDs
  const versionsQueryOptions = useMissionAssessmentVersionsByTechnologyQueryOptions(
    shouldFetchMissionAssessments ? technologyId : undefined,
  );
  const {
    data: versions,
    isLoading: isVersionsLoading,
    isFetching: isVersionsFetching,
  } = useQuery(versionsQueryOptions);

  const latestAccessibleMissionAssessmentId = useMemo(() => {
    if (!versions || versions.length === 0) {
      return null;
    }

    const latestAccessibleVersion = versions.find((version) => !version.accessRestricted);
    return latestAccessibleVersion?.id ?? null;
  }, [versions]);

  // Determine which MA ID to display: URL param (maId) when accessible, otherwise latest accessible
  const selectedMissionAssessmentId = useMemo(() => {
    if (!versions) {
      // Avoid fetching core details until versions resolve access for URL-provided maId.
      return null;
    }

    if (!maId) {
      return latestAccessibleMissionAssessmentId;
    }

    const selectedVersion = versions.find((version) => version.id === maId);
    if (!selectedVersion || selectedVersion.accessRestricted) {
      return latestAccessibleMissionAssessmentId;
    }

    return maId;
  }, [latestAccessibleMissionAssessmentId, maId, versions]);

  const missionAssessmentIdForQuery = shouldFetchMissionAssessments
    ? (selectedMissionAssessmentId ?? undefined)
    : undefined;

  // Fetch core data for the selected MA (lightweight - no effects, inters, services, etc.)
  const coreQueryOptions = useMissionAssessmentCoreQueryOptions(missionAssessmentIdForQuery);
  const {
    data: missionAssessmentCore,
    isLoading: isCoreLoading,
    isFetching: isCoreFetching,
  } = useQuery(coreQueryOptions);

  // Reset maId if it belongs to a different technology
  useEffect(() => {
    if (!maId || !missionAssessmentCore || !technologyId) {
      return;
    }

    const missionAssessmentTechnologyId = missionAssessmentCore.technology?.id;

    if (missionAssessmentTechnologyId && missionAssessmentTechnologyId !== technologyId) {
      void setMaId(null);
    }
  }, [maId, missionAssessmentCore, setMaId, technologyId]);

  // Keep URL maId synchronized with access controls (restricted/non-existent IDs fallback to accessible latest)
  useEffect(() => {
    if (!versions || !maId) {
      return;
    }

    const selectedVersion = versions.find((version) => version.id === maId);
    if (selectedVersion && !selectedVersion.accessRestricted) {
      return;
    }

    void setMaId(latestAccessibleMissionAssessmentId);
  }, [latestAccessibleMissionAssessmentId, maId, setMaId, versions]);

  useEffect(() => {
    if (!isTechnologyAccessForbidden) {
      return;
    }

    void navigate(isIntel ? routes.intelTechnologies.path : routes.missionAssessments.path, { replace: true });
  }, [isIntel, isTechnologyAccessForbidden, navigate]);

  // Validate that the MA belongs to the current technology
  const displayedMissionAssessment = useMemo(() => {
    if (!missionAssessmentCore) {
      return null;
    }

    if (!technologyId) {
      return missionAssessmentCore;
    }

    const assessmentTechnologyId = missionAssessmentCore.technology?.id;
    if (assessmentTechnologyId && assessmentTechnologyId !== technologyId) {
      return null;
    }

    return missionAssessmentCore;
  }, [missionAssessmentCore, technologyId]);

  // Loading state: versions loading OR (versions loaded but core loading for selected ID)
  const isLoadingDisplayedMissionAssessment =
    isVersionsLoading || (Boolean(selectedMissionAssessmentId) && isCoreLoading);

  const showTileLoading = Boolean(!displayedMissionAssessment && isLoadingDisplayedMissionAssessment);

  // Transitioning: we have data but fetching new data
  const isTransitioning = Boolean(displayedMissionAssessment && (isVersionsFetching || isCoreFetching));

  const getHelmetTitle = (): string => {
    switch (selectedTab) {
      case 'details':
        return 'Technology Details';
      case 'cyber':
        return 'Cyber Assessment';
      case 'ma':
        return 'Mission Assessment';
      case 'mrl':
        return 'MRL Assessment';
      case 'organization':
        return 'Organization';
      case 'overview':
        return 'Technology Overview';
      case 'trl':
        return 'TRL Assessment';
      default:
        return 'Technology';
    }
  };

  const helmetTitle = getHelmetTitle();

  const shouldShowEmptyState = !isInnovator && !displayedMissionAssessment && !isVersionsLoading && !isCoreLoading;

  const onToggleTab = (tab: TechnologyHeaderTab) => {
    void setSelectedTab(tab);

    // Reset subtab to overview when navigating to TRL or MRL tabs
    if (tab === 'trl') {
      void setTrlTab('overview');
      void setTrlAssessmentId(null);
      // Clear MRL assessment ID when switching away from MRL tab
      void setMrlAssessmentId(null);
    } else if (tab === 'mrl') {
      void setMrlTab('overview');
      void setMrlAssessmentId(null);
      // Clear TRL assessment ID when switching away from TRL tab
      void setTrlAssessmentId(null);
    } else {
      // Clear both assessment IDs when switching to any other tab (overview, details, ma)
      void setMrlAssessmentId(null);
      void setTrlAssessmentId(null);
    }
  };

  return {
    displayedMissionAssessment,
    helmetTitle,
    isInnovator,
    isLoadingDisplayedMissionAssessment,
    isLoadingTechnology,
    isTechnologyAccessForbidden,
    isTransitioning,
    isVersionsLoading,
    maVersions: versions ?? [],
    onToggleTab,
    selectedTab,
    shouldShowEmptyState,
    showTileLoading,
    technology,
    technologyFallbackPath: isIntel ? routes.intelTechnologies.path : routes.technologies.path,
  };
};
